import { graphqlClient } from '@/lib/graphql-client';
import { mergeWhere } from '@/lib/table-query';
import { buildGetMbmCustomersQuery, buildGetMbmProjectsQuery } from '../graphql/queries';
import { INSERT_MBM_PROJECT_MUTATION, UPDATE_MBM_PROJECT_MUTATION } from '../graphql/mutations';
import { MbmProjectFormValues } from '../schemas/mbm-project-form-schema';
import {
  GetMbmProjectsParams,
  MbmCustomer,
  MbmCustomersResult,
  MbmProject,
  MbmProjectsPagingResult,
} from '../types/mbm-project.types';
import { toDistinctCustomers } from '../utils/mbm-customers';
import { buildMbmProjectFilter } from '../utils/mbm-project-filter';
import {
  buildMbmProjectInsertInput,
  buildMbmProjectUpdateInput,
} from '../utils/mbm-project-payload';

// The customer filter is built from the projects themselves, so it can only offer customers that
// appear in the first page of this size, the same ceiling the timesheet pickers already use.
const CUSTOMER_LOOKUP_PAGE_SIZE = 500;

export const getMbmProjects = async (
  params: GetMbmProjectsParams
): Promise<{ MbmProjects: MbmProjectsPagingResult }> => {
  const { pageNo, pageSize, search, customerId, status, filter, order } = params;
  return graphqlClient.query({
    query: buildGetMbmProjectsQuery(
      mergeWhere(buildMbmProjectFilter({ search, customerId, status }), filter),
      pageNo,
      pageSize,
      order
    ),
  }) as Promise<{ MbmProjects: MbmProjectsPagingResult }>;
};

export const getMbmCustomers = async (): Promise<MbmCustomer[]> => {
  const result = await graphqlClient.query<{ MbmProjects: MbmCustomersResult }>({
    query: buildGetMbmCustomersQuery({}, 1, CUSTOMER_LOOKUP_PAGE_SIZE),
  });
  return toDistinctCustomers(result.MbmProjects.items);
};

/**
 * The legacy upsert identified a project by its customer id and project id together. Only the
 * count matters here, so it borrows the two-column query instead of fetching whole projects.
 */
export const mbmProjectExists = async (customerId: string, projectId: string): Promise<boolean> => {
  const result = await graphqlClient.query<{ MbmProjects: { totalCount: number } }>({
    query: buildGetMbmCustomersQuery(
      { CustomerId: customerId.trim(), ProjectId: projectId.trim() },
      1,
      1
    ),
  });
  return result.MbmProjects.totalCount > 0;
};

export const createMbmProject = async (values: MbmProjectFormValues) => {
  return graphqlClient.mutate({
    query: INSERT_MBM_PROJECT_MUTATION,
    variables: { input: buildMbmProjectInsertInput(values) },
  });
};

/** Resolves to `false` without calling the API when the values match what is stored. */
export const updateMbmProject = async (params: {
  project: MbmProject;
  values: MbmProjectFormValues;
}): Promise<boolean> => {
  const input = buildMbmProjectUpdateInput(params.project, params.values);
  if (!input) return false;

  await graphqlClient.mutate({
    query: UPDATE_MBM_PROJECT_MUTATION,
    variables: { filter: JSON.stringify({ _id: params.project.ItemId }), input },
  });
  return true;
};
